import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { PersonPlacementEvent } from "../../../api/threads/placementsApi";
import type { ThreadSummary } from "../../../api/threads/threadDecoders";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { appPaths } from "../../../shared/routing/appPaths";
import Chip from "../../../ui/feedback/Chip";
import { classNames } from "../../../ui/styles/classNames";
import { useThreadRowFacts } from "../data/useThreadRowFacts";
import ThreadConnectionSign from "../status/ThreadConnectionSign";
import { outranksWoke, rowStatus } from "../status/runStateLabels";
import ThreadStatusBadge from "../status/ThreadStatusBadge";
import type { InboxPlace } from "./inboxPlacement";
import ThreadFiledUnder from "./ThreadFiledUnder";
import ThreadRowActions from "./ThreadRowActions";
import ThreadRowMeta from "./ThreadRowMeta";
import ToolApprovalInboxHint from "./ToolApprovalInboxHint";

interface ThreadListRowProps {
  summary: ThreadSummary;
  /** `null` when the viewer hosts the Thread. */
  hostName: string | null;
  selected: boolean;
  /** Where the Thread sits in the viewer's list (F44). */
  place: InboxPlace;
  /** ISO 8601, while snoozed: when it wakes. */
  snoozedUntil: string | null;
  /** Pinned by the viewer. */
  pinned: boolean;
  onPlace: (event: PersonPlacementEvent, snoozedUntil?: Date) => void;
  /** Dragging, as in T3 Code's sidebar; absent for a row that doesn't move (a working Thread). */
  drag?: RowDrag;
  /** A review or result of this Run waits for the viewer (the sidebar's inbox): a ready Run then shows Input. */
  waitsForMe?: boolean;
}

export interface RowDrag {
  readonly onStart: () => void;
  readonly onEnd: () => void;
  /** On a pin: a Thread dropped on this row goes before it (upper half) or after it (lower half). */
  readonly onDropOnRow?: (before: boolean) => void;
  /** On a pin: Alt+↑ / Alt+↓ moves it one place (keyboard reordering). */
  readonly onMove?: (direction: "up" | "down") => void;
}

/** The drag's payload type: a Thread ID, which other pages ignore. */
export const THREAD_DRAG_TYPE = "application/x-dmw-thread";

/**
 * One Thread in the sidebar: the title has the row's full width and wraps to two lines;
 * below it one line holds the status, the subject and, at the right, the last activity.
 * Pin, settle and snooze are small and take the last activity's place on hover or focus
 * (F44, F68, as in T3 Code), so they never cover the title; a woken Thread carries "Woke"
 * until it is opened.
 */
export default function ThreadListRow({ summary, hostName, selected, place, snoozedUntil, pinned, onPlace, drag, waitsForMe = false }: ThreadListRowProps) {
  const { t, i18n } = useTranslation();
  const facts = useThreadRowFacts(summary);
  const unread = facts.unread && !selected;
  // T3 Code's precedence (23:36): Approval, Input, Working and Failed show over "Woke"; "Woke" shows over Done.
  const status = rowStatus(facts.status, waitsForMe);
  const woke = place === "woke" && !outranksWoke(status);
  // The requests the line's waits carry, as the host's PC reports them (W-22).
  const approvals = (summary.projection.waits ?? []).flatMap((wait) => (wait.approval == null ? [] : [wait.approval]));
  const waiting = summary.projection.open_human_step;

  return (
    <li
      className="group relative"
      draggable={drag !== undefined}
      onDragStart={
        drag === undefined
          ? undefined
          : (event) => {
              event.dataTransfer.setData(THREAD_DRAG_TYPE, summary.threadId);
              event.dataTransfer.effectAllowed = "move";
              drag.onStart();
            }
      }
      onDragEnd={drag?.onEnd}
      onDragOver={drag?.onDropOnRow === undefined ? undefined : (event) => { event.preventDefault(); }}
      onDrop={
        drag?.onDropOnRow === undefined
          ? undefined
          : (event) => {
              event.preventDefault();
              event.stopPropagation();
              const box = event.currentTarget.getBoundingClientRect();
              drag.onDropOnRow?.(event.clientY < box.top + box.height / 2);
            }
      }
      onKeyDown={
        drag?.onMove === undefined
          ? undefined
          : (event) => {
              if (!event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
              event.preventDefault();
              drag.onMove?.(event.key === "ArrowUp" ? "up" : "down");
            }
      }
    >
      <Link
        to={appPaths.thread(summary.threadId)}
        className={classNames("app-nav-item block space-y-0.5 px-2 py-1.5 text-sm", selected && "app-nav-item-active")}
        aria-current={selected ? "page" : undefined}
        aria-keyshortcuts={drag?.onMove === undefined ? undefined : "Alt+ArrowUp Alt+ArrowDown"}
      >
        {/* His row (4 Nov 23:17): the name over two lines, then the meta line; no column kept for a dot. Unread reads bold. */}
        {unread ? <span className="sr-only">{t("threads.list.unread")}</span> : null}
        <span className={classNames("line-clamp-2 block break-words leading-snug", unread ? "font-semibold" : "font-medium")} title={summary.title}>
          {summary.title}
        </span>
        {/* The subject gives way down to a few characters; where even that does not fit, the
            time and host move below, still at the right. */}
        <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {/* First, so it stays while the actions take the host's and time's place. */}
          <ThreadConnectionSign state={summary.state} />
          {woke ? (
            <Chip tone="warning" size="compact" title={t("threads.list.placement.wokeTitle")}>{t("threads.list.placement.woke")}</Chip>
          ) : null}
          {woke ? null : <ThreadStatusBadge status={status} waiting={waiting ?? null} hostName={hostName} seen={!unread} compact={true} />}
          {place === "snoozed" && snoozedUntil !== null ? (
            <span className="app-text-muted text-xs">{t("threads.list.placement.snoozedUntil", { time: formatDateTime(snoozedUntil, i18n.resolvedLanguage) })}</span>
          ) : null}
          <ThreadFiledUnder summary={summary} className="min-w-0 flex-1 basis-10" />
          <ThreadRowMeta hostName={hostName} lastActivityAt={facts.lastActivityAt} fork={summary.parent !== null} className="ml-auto max-w-full group-focus-within:invisible group-hover:invisible" />
        </span>
        {hostName === null && approvals.length > 0 ? <ToolApprovalInboxHint approvals={approvals} connectorId={summary.attachedConnector} /> : null}
      </Link>
      {/* On the second line's right, where the last activity is; above the rows below it. */}
      <span className="absolute bottom-1 right-1 z-10 flex items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <ThreadRowActions
          title={summary.title}
          place={place}
          pinned={pinned}
          canSettleOrSnooze={summary.canSettleOrSnooze}
          onPlace={onPlace}
          rename={hostName === null && summary.state !== "closed" ? { threadId: summary.threadId, lastEntryNumber: summary.lastEntryNumber } : null}
        />
      </span>
    </li>
  );
}
